import { isDeclinedEvent } from "./eventResponse";

const getEventTime = (value) => {
  const time = Number(value);
  return Number.isFinite(time) ? time : null;
};

const normalizeText = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

export const getConfiguredMeetingBotEmail = () =>
  normalizeText(process.env.REACT_APP_MEETING_BOT_EMAIL) || "bot@gaintime.ai";

export const isConfiguredMeetingBotEmail = (value) =>
  normalizeText(value) === getConfiguredMeetingBotEmail();

const attendeeEmails = (event) =>
  (Array.isArray(event?.attendees) ? event.attendees : [])
    .map((attendee) => normalizeText(attendee?.email))
    .filter((attendeeEmail) =>
      attendeeEmail && !isConfiguredMeetingBotEmail(attendeeEmail)
    )
    .sort();

const hasEligibleAttendees = (event) => attendeeEmails(event).length > 0;

export const buildNextMeetingEventFingerprint = (event) => {
  if (!event?.eventId) return null;

  return JSON.stringify({
    eventId: event.eventId,
    title: event.title || "",
    startDateTime: getEventTime(event.startDateTime),
    endDateTime: getEventTime(event.endDateTime),
    attendees: attendeeEmails(event),
    organizerEmail: normalizeText(event.organizer?.email),
  });
};

export const buildNextMeetingAnalysisTarget = (meeting) => {
  const eventFingerprint = buildNextMeetingEventFingerprint(meeting);
  if (!meeting?.eventId || !eventFingerprint) return null;

  return {
    eventId: meeting.eventId,
    eventFingerprint,
    meeting,
  };
};

export const buildNextMeetingAnalysisTargets = (candidate) => {
  if (candidate?.status === "single") {
    return [buildNextMeetingAnalysisTarget(candidate.meeting)].filter(Boolean);
  }

  if (candidate?.status === "conflict") {
    return (Array.isArray(candidate.meetings) ? candidate.meetings : [])
      .map(buildNextMeetingAnalysisTarget)
      .filter(Boolean);
  }

  return [];
};

const sortFutureMeetings = (a, b) => {
  const startDiff = getEventTime(a.startDateTime) - getEventTime(b.startDateTime);
  if (startDiff !== 0) return startDiff;

  const titleDiff = String(a.title || "").localeCompare(String(b.title || ""));
  if (titleDiff !== 0) return titleDiff;

  return String(a.eventId || "").localeCompare(String(b.eventId || ""));
};

export const resolveNextMeetingCardCandidate = (events = [], now = Date.now()) => {
  const futureMeetings = events
    .filter((event) => {
      const startTime = getEventTime(event?.startDateTime);
      return (
        startTime !== null &&
        startTime >= now &&
        !isDeclinedEvent(event) &&
        hasEligibleAttendees(event)
      );
    })
    .sort(sortFutureMeetings);

  if (!futureMeetings.length) {
    return { status: "empty", meeting: null, meetings: [] };
  }

  const earliestStartTime = getEventTime(futureMeetings[0].startDateTime);
  const sameStartMeetings = futureMeetings.filter(
    (event) => getEventTime(event.startDateTime) === earliestStartTime
  );

  if (sameStartMeetings.length > 1) {
    return {
      status: "conflict",
      meeting: null,
      meetings: sameStartMeetings,
      startDateTime: earliestStartTime,
    };
  }

  return {
    status: "single",
    meeting: futureMeetings[0],
    meetings: [futureMeetings[0]],
    eventFingerprint: buildNextMeetingEventFingerprint(futureMeetings[0]),
  };
};

export const isMatchingNextMeetingAnalysis = (analysis, candidate) => {
  if (!analysis || candidate?.status !== "single") return false;
  return (
    analysis.analysisEventId === candidate.meeting?.eventId &&
    analysis.eventFingerprint === candidate.eventFingerprint
  );
};

const serializeTimestamp = (value) => {
  if (!value) return "";
  if (typeof value.toMillis === "function") return String(value.toMillis());
  if (value instanceof Date) return String(value.getTime());
  if (typeof value === "object") {
    const seconds = Number(value.seconds ?? value._seconds);
    const nanoseconds = Number(value.nanoseconds ?? value._nanoseconds);
    if (Number.isFinite(seconds) || Number.isFinite(nanoseconds)) {
      return `${Number.isFinite(seconds) ? seconds : ""}:${
        Number.isFinite(nanoseconds) ? nanoseconds : ""
      }`;
    }
  }
  return String(value);
};

export const buildMeetingCostInfoSignature = (meeting, orgId) => {
  if (!orgId) return null;

  const costInfo = meeting?.costInfo?.[orgId];
  if (!costInfo || typeof costInfo !== "object") return null;

  return JSON.stringify({
    amount: Number.isFinite(Number(costInfo.amount)) ? Number(costInfo.amount) : null,
    currency: costInfo.currency || "",
    updatedAt: serializeTimestamp(costInfo.DataUpdationTimestamp),
  });
};
